import { useCallback, useEffect, useRef, useState } from 'react';

/** RL-1658: how long Undo is offered before the action is sent. */
export const UNDO_MS = 10_000;

/** How a waiting action is sent: `keepalive` when the tab is closing. */
export type DeferredRun = (opts: { keepalive: boolean }) => Promise<unknown>;

/**
 * RL-1658: "Cancel ticket" and "Done" take the card away at once and wait
 * 10 seconds before calling the API, so Undo needs no API call and the
 * patient is only told about a cancel that stays. Review fixes: leaving the
 * page in the app sends what is waiting at once; closing or reloading the
 * tab first asks (beforeunload) and, if the person leaves anyway, sends it
 * with `keepalive` so the request outlives the page. `pause` / `resume` hold
 * the timer while the Undo toast is hovered or focused.
 */
export function useDeferredActions() {
  const [hidden, setHidden] = useState<ReadonlySet<string>>(new Set());
  const pending = useRef(
    new Map<
      string,
      {
        timer: ReturnType<typeof setTimeout> | null;
        run: DeferredRun;
        left: number;
        since: number;
      }
    >(),
  );
  const show = useCallback((id: string) => {
    setHidden((all) => {
      const next = new Set(all);
      next.delete(id);
      return next;
    });
  }, []);

  const commit = useCallback(
    (id: string, keepalive = false) => {
      const p = pending.current.get(id);
      if (!p) return;
      if (p.timer) clearTimeout(p.timer);
      pending.current.delete(id);
      void p.run({ keepalive }).finally(() => show(id));
    },
    [show],
  );

  const arm = useCallback(
    (id: string, ms: number) => {
      const p = pending.current.get(id);
      if (!p) return;
      p.left = ms;
      p.since = Date.now();
      p.timer = setTimeout(() => commit(id), ms);
    },
    [commit],
  );

  const defer = useCallback(
    (id: string, run: DeferredRun) => {
      if (pending.current.has(id)) return;
      setHidden((all) => new Set(all).add(id));
      pending.current.set(id, { timer: null, run, left: UNDO_MS, since: 0 });
      arm(id, UNDO_MS);
    },
    [arm],
  );

  const undo = useCallback(
    (id: string) => {
      const p = pending.current.get(id);
      if (!p) return;
      if (p.timer) clearTimeout(p.timer);
      pending.current.delete(id);
      show(id);
    },
    [show],
  );

  /** Hold the timer (the Undo toast is hovered or focused). */
  const pause = useCallback((id: string) => {
    const p = pending.current.get(id);
    if (!p?.timer) return;
    clearTimeout(p.timer);
    p.timer = null;
    p.left = Math.max(0, p.left - (Date.now() - p.since));
  }, []);

  /** Start it again with the time that was left. */
  const resume = useCallback(
    (id: string) => {
      const p = pending.current.get(id);
      if (!p || p.timer) return;
      arm(id, p.left);
    },
    [arm],
  );

  useEffect(() => {
    const all = pending.current;
    const warn = (e: BeforeUnloadEvent) => {
      if (!all.size) return;
      e.preventDefault();
      // Older browsers need returnValue set to show the prompt.
      e.returnValue = '';
    };
    const flushKeepalive = () => {
      for (const id of [...all.keys()]) commit(id, true);
    };
    window.addEventListener('beforeunload', warn);
    window.addEventListener('pagehide', flushKeepalive);
    return () => {
      window.removeEventListener('beforeunload', warn);
      window.removeEventListener('pagehide', flushKeepalive);
      // Leaving the queue inside the app: send what is waiting now.
      for (const id of [...all.keys()]) commit(id);
    };
  }, [commit]);

  return { hidden, defer, undo, pause, resume };
}
